

import { useEffect, useState } from "react";
import { Check, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utility/apiInstance";
import {
  MileStoneError,
  MilestoneState,
  MileStoneUpdateOfOceanState,
  TabFSUSProps,
} from "./types/MilestoneType";
import { mapToOceanMilestoneState } from "./utils";
import { error } from "console";

// ── Styles ────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  root: { padding: "14px 0 0" },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  sectionTitle: { display: "flex", alignItems: "center", gap: "7px" },
  sectionAccent: { width: "3px", height: "16px", background: "#F8B71F", borderRadius: "99px" },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  rowCount: { fontSize: "11px", color: "#9ca3af", fontWeight: 500 },

  colHeaders: {
    display: "grid",
    gridTemplateColumns: "220px 220px 160px 100px",
    padding: "0 12px",
    marginBottom: "6px",
    minWidth: "700px",
  },
  colHeader: {
    fontSize: "10.5px",
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    padding: "0 5px",
  },

  rowWrap: { position: "relative", marginBottom: "6px" },
  rowCard: {
    display: "grid",
    gridTemplateColumns: "220px 220px 160px 100px",
    background: "#fff",
    border: "1px solid #efefef",
    borderRadius: "10px",
    padding: "9px 12px",
    alignItems: "center",
    transition: "box-shadow 0.15s, border-color 0.15s",
    minWidth: "700px",
  },
  rowCardCompleted: { background: "#f9fafb" },
  rowCardPending: { borderLeft: "3px solid #F8B71F" },
  rowCardHovered: { borderColor: "#e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cell: { padding: "0 5px" },

  milestoneName: { fontSize: "12.5px", fontWeight: 600, color: "#1a1a1a" },
  milestoneNameCompleted: { fontSize: "12.5px", fontWeight: 500, color: "#6b7280" },

  locVal: { fontSize: "12px", color: "#374151" },
  locNa: { fontSize: "12px", color: "#9ca3af" },

  dateVal: { fontSize: "12px", color: "#374151" },
  dateNa: { fontSize: "12px", color: "#9ca3af" },
  dateInput: {
    border: "1px solid #e2e2e2",
    borderRadius: "6px",
    padding: "5px 7px",
    fontSize: "11px",
    fontFamily: "inherit",
    color: "#374151",
    background: "#fff",
    outline: "none",
    width: "140px",
    cursor: "pointer",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },

  badgeDone: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "11px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "5px",
  },
  badgePending: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#fef9c3",
    color: "#854d0e",
    fontSize: "11px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "5px",
  },

  errText: {
    color: "#ef4444",
    fontSize: "10.5px",
    marginTop: "3px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
  },
};

function ErrorHint({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={s.errText}>
      <AlertCircle size={10} />
      {message}
    </div>
  );
}

export function TabFsusOcean({ awbNumber, isOnline }: TabFSUSProps) {
  const [milestones, setMilestones] = useState<MileStoneUpdateOfOceanState[]>([]);
  const [milestoneError, setMilestoneError] = useState<MileStoneError>({
    pieces: "",
    weight: "",
  });
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const fetchMilestoneOfAwbNumber = async (awbNumber: string) => {
    if (!awbNumber) return null;
    try {
      const { data } = await axiosInstance.get(
        `/api/v1/offline-ocean-milestone/${awbNumber}?isOnline=${isOnline}`
      );
      const flatEvents: MileStoneUpdateOfOceanState[] = mapToOceanMilestoneState(data.data, isOnline);
      setMilestones(flatEvents);
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    fetchMilestoneOfAwbNumber(awbNumber);
  }, []);

  const handleChange = async (
    index: number,
    field: keyof MilestoneState,
    value: any
  ) => {
    if (field == "pieces" || field == "weight") {
      if (!/^\d+(\.\d*)?$/.test(value) && value !== "") return;
      if (value === "") {
        let error = `${field} must be greater than 0`;
        setMilestoneError((prev) => ({ ...prev, [field]: error }));
        return;
      }
    }

    const updatedMilestones = milestones.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );

    setMilestones(updatedMilestones);

    if (field === "date") {
      try {
        await axiosInstance.post("/api/v1/offline-ocean-milestone/create", updatedMilestones);
        field === "date" && fetchMilestoneOfAwbNumber(awbNumber);
        toast.success("Milestones updated");
      } catch (error:any) {
        const errorMessage=error.response.data.error||"Failed to update."
        toast.error(errorMessage);
      }
    }
  };

  const completedCount = milestones.filter((m) => m.status === 1).length;
  const COLS = ["Milestone", "Locations", "Date", "Status"];

  return (
    <div style={s.root}>
      <div style={s.sectionHeader}>
        <div style={s.sectionTitle}>
          <div style={s.sectionAccent} />
          <span style={s.sectionLabel}>Ocean Status Updates</span>
        </div>
        {milestones.length > 0 && (
          <span style={s.rowCount}>
            {milestones.length} milestone{milestones.length !== 1 ? "s" : ""}
            {completedCount > 0 && ` · ${completedCount} completed`}
          </span>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={s.colHeaders}>
          {COLS.map((h) => (
            <span key={h} style={s.colHeader}>
              {h}
            </span>
          ))}
        </div>

        {milestones.map((event: MileStoneUpdateOfOceanState, index: number) => {
          const isCompleted = event.status === 1;
          const isHovered = hoveredRow === index;

          const rowStyle: React.CSSProperties = {
            ...s.rowCard,
            ...(isCompleted ? s.rowCardCompleted : s.rowCardPending),
            ...(isHovered ? s.rowCardHovered : {}),
          };

          return (
            <div
              key={index + 1}
              style={s.rowWrap}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
            >
              <div style={rowStyle}>
                <div style={s.cell}>
                  <span style={isCompleted ? s.milestoneNameCompleted : s.milestoneName}>
                    {event.mileStone}
                  </span>
                </div>

                <div style={s.cell}>
                  {event.portName ? (
                    <span style={s.locVal}>{event.portName}</span>
                  ) : (
                    <span style={s.locNa}>N/A</span>
                  )}
                </div>

                <div style={s.cell}>
                  {event.status == 1 ? (
                    <span style={s.dateVal}>{event.date}</span>
                  ) : isOnline == true ? (
                    <span style={event?.expectedDate ? s.dateVal : s.dateNa}>
                      {event?.expectedDate || "N/A"}
                    </span>
                  ) : (
                    <>
                      <input
                        style={s.dateInput}
                        type="date"
                        value={
                          event?.date ||
                          (event?.expectedDate ? event.expectedDate.split("T")[0] : "")
                        }
                        onMouseDown={(e: any) => {
                          e.preventDefault();
                          e.target.showPicker?.();
                        }}
                        onChange={(e) => handleChange(index, "date", e.target.value)}
                      />
                      <ErrorHint message={milestoneError.pieces || milestoneError.weight} />
                    </>
                  )}
                </div>

                <div style={s.cell}>
                  {event.status === 1 ? (
                    <span style={s.badgeDone}>
                      <Check size={10} />
                      Done
                    </span>
                  ) : (
                    <span style={s.badgePending}>Pending</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}




















